import React, { useState } from 'react';
import i18n from 'util/i18n';
import { lettersAndNumbersPattern } from 'constants/patterns';
import { DEFAULT_LATEST, SELF } from 'constants/dateLimits';
import { DateModel, FieldModel } from 'forms/shared/types';
import { toMmddyyyy } from 'helpers/date';
import { ErrorCode } from '@redux/modules/application/reducers/error';
import Text from '../Text';
import DateControl from '../DateControl';

const YEAR_ERROR_CODES: readonly string[] = [
  'date.year.min',
  'date.year.max'
];

const SSN_ERROR_CODES: readonly string[] = [
  'pattern',
];

const preventSpecialAndSpace = (value = '') => {
  let result = value;
  if ((/[ ?!@#$%^&*)(-]/.exec(value))) {
    result = value.replace(/[ ?.,?!@#$%^&*)(-]/g, '');
  }
  return result;
};

export type ChallengeQuestionsProps = {
  identityVerificationResponseReceived: boolean;
  submitChallengeResponse: (ssn: string, dob: string) => void;
  successfulIdentityVerification: boolean;
};

const ChallengeQuestions = function (props: ChallengeQuestionsProps) {
  const [ssn, setSsn] = useState<string>('');
  const [dob, setDob] = useState<DateModel>({} as DateModel);
  const [ssnIsValid, setSsnIsValid] = useState<boolean>(false);
  const [dobIsValid, setDobIsValid] = useState<boolean>(false);

  const onUpdateSsn = (queue: FieldModel) => {
    const ssnValue = (queue?.value || '').toUpperCase();
    setSsn(ssnValue);
  };

  const onUpdateDob = (queue: DateModel) => {
    setDob(queue);
  };

  const makeSsnStateFromErrors = (errors: ErrorCode[]) => {
    const error = Boolean(ssn && errors.filter((x) => SSN_ERROR_CODES.includes(x.code)).some((x) => x.valid !== true));
    const valid = errors.filter((x) => SSN_ERROR_CODES.includes(x.code)).every((x) => x.valid === true);

    setSsnIsValid(valid);

    return {
      error,
      valid,
    };
  };

  const makeYearStateFromErrors = (errors: ErrorCode[]) => {
    const error = Boolean(dob?.year && errors.filter((x) => YEAR_ERROR_CODES.includes(x.code)).some((x) => x.valid !== true));
    const valid = errors.filter((x) => YEAR_ERROR_CODES.includes(x.code)).every((x) => x.valid === true);

    setDobIsValid(valid);

    return {
      error,
      valid,
    };
  };

  const renderVerificationError = () => (
    <div className="field no-margin-bottom">
      <div className="table">
        <div className="usa-alert usa-alert-error" role="alert">
          <div className="usa-alert-body">
            <h5 className="usa-alert-heading">{i18n.t('challenge.errorTitle')}</h5>
            <p>{i18n.t('challenge.errorMessage')}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const onSubmit = () => {
    props.submitChallengeResponse(ssn, toMmddyyyy(dob));
  };

  return (
    <div className="challenge-questions">
      <h4>{i18n.t('challenge.ssn')}</h4>
      <Text
        className="verification-ssn usa-small-input"
        maxlength="9"
        minlength="9"
        pattern="^[0-9a-zA-Z]{9}$"
        prefilter={preventSpecialAndSpace}
        clipboard={false}
        value={ssn}
        onUpdate={onUpdateSsn}
        required
        autocorrect={false}
        autocapitalize={false}
        spellcheck="false"
        inputFilter={/[^0-9a-zA-Z]/g}
        forceValidation
        makeStateFromErrors={makeSsnStateFromErrors}
      />

      <h4>{i18n.t('challenge.dob')}</h4>
      <DateControl
        {...dob} // eslint-disable-line react/jsx-props-no-spreading
        name="birthdate"
        relationship="Self"
        minDate={SELF.earliest}
        maxDate={DEFAULT_LATEST}
        onUpdate={onUpdateDob}
        required
        showEstimated={false}
        useMinYear
        forceYearValidation
        makeYearStateFromErrors={makeYearStateFromErrors}
      />

      {props.identityVerificationResponseReceived && !props.successfulIdentityVerification && renderVerificationError()}

      <button
        className="submit usa-button-big"
        aria-label={`${i18n.t('challenge.submit')}`}
        onClick={onSubmit}
        onKeyPress={onSubmit}
        type="button"
        disabled={!ssnIsValid || !dobIsValid}
      >
        <span>{i18n.t('challenge.submit')}</span>
      </button>
    </div>
  );
};

export default ChallengeQuestions;
